import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import {
  Bot, GraduationCap, Briefcase, BookOpen, Users, ChevronRight,
  Sparkles, Target, TrendingUp, Award, Shield, Zap, Globe, Brain
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

import SEOHead from './SEOHead';
import Footer from './Footer';

const LandingPage = () => {
  const { user, openAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [ambitionInput, setAmbitionInput] = useState('');
  const [isLoadingAmbition, setIsLoadingAmbition] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleAmbitionSubmit = async (e) => {
    e.preventDefault();
    
    if (!ambitionInput.trim()) {
      toast.error('Please enter your ambition');
      return;
    }

    if (!user) {
      toast.info('Please sign in to use Ambition AI');
      openAuth('login');
      return;
    }

    setIsLoadingAmbition(true);
    try {
      const response = await axios.post(`${API}/api/career-path`, 
        { ambition: ambitionInput },
        { 
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          } 
        }
      );

      if (response.data.success) {
        navigate('/career-path', { state: { careerPath: response.data.career_path, ambitionText: ambitionInput } });
      } else {
        toast.error(response.data.message || 'Failed to process your ambition');
      }
    } catch (error) {
      console.error('Ambition submission error:', error);
      if (error.response?.status === 402) {
        toast.error('Insufficient credits. Please purchase more credits to continue.');
        setTimeout(() => navigate('/buy-credits'), 1500);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to process your ambition');
      }
    } finally {
      setIsLoadingAmbition(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#050508] via-[#0a0a10] to-[#050508]">
      <SEOHead
        title="AI Academic Companion for Students"
        description="Nerd Nation — Your Ambition is Our Mission. AI-powered exam prep, career advice, global scholarships, student jobs and a community built for ambitious students."
        path="/"
      />
      <section className="relative pt-24 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Radial Gradient Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-radial from-gold-primary/20 via-transparent to-transparent blur-3xl opacity-30"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="hover:scale-105 transition-transform duration-500">
                <img 
                  src="https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/wpzfhpfx_Time%284%29.png" 
                  alt="Nerd Nation"
                  className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 object-contain max-w-none"
                  onError={(e) => console.error('User logo failed to load')}
                  onLoad={() => console.log('User logo loaded successfully!')} loading="eager" fetchpriority="high" decoding="async" />
              </div>
            </div>
            
            <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl font-bold mb-4 max-w-5xl mx-auto leading-tight">
              <span className="text-white">Nerd </span>
              <span className="gradient-text-gold">Nation</span>
            </h1>
            
            <p className="font-outfit text-2xl md:text-3xl lg:text-4xl text-gray-300 mb-6 max-w-4xl mx-auto leading-relaxed font-semibold">
              You Can Achieve Any Ambition With Us !
            </p>
            
            <p className="font-outfit text-lg md:text-xl text-gray-400 mb-12 max-w-3xl mx-auto font-medium">
              Career Guidance to Global Exams—why wait? Ask Nerd AI !
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center w-full max-w-md mx-auto sm:max-w-none">
              <Button
                size="lg"
                onClick={() => openAuth('register')}
                className="w-full sm:w-auto bg-gradient-to-r from-gold-dark via-gold-primary to-gold-light hover:from-gold-light hover:via-gold-primary hover:to-gold-dark text-[#0a0a10] text-lg px-10 py-6 h-auto font-bold min-w-[220px] rounded-xl shadow-[0_8px_32px_rgba(240,180,41,0.4)] hover:shadow-[0_12px_48px_rgba(240,180,41,0.6)] transition-all duration-300 hover:scale-105"
                data-testid="hero-signup-button"
              >
                Start Learning Free
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => openAuth('login')}
                className="w-full sm:w-auto bg-transparent border-2 border-blue-primary/40 text-blue-light hover:bg-blue-primary/10 hover:border-blue-primary text-lg px-10 py-6 h-auto font-bold min-w-[220px] rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_8px_32px_rgba(59,130,246,0.3)]"
                data-testid="hero-login-button"
              >
                Sign In
              </Button>
            </div>
            
            <p className="font-outfit text-sm text-gray-500 mt-6 tracking-wider">
              Try Free Features • Premium Plans Available
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-[#0a0a10]/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Nerd-AI: Career Success Platform
            </h2>
            <p className="font-outfit text-xl md:text-2xl text-gray-400 max-w-4xl mx-auto font-medium leading-relaxed">
              Global Exams: GRE, GMAT, Medical, Dental & Pharmacy Exams to IIT-JEE & NEET UG, We've got you covered !
            </p>
          </div>
          
          {/* Feature Banners */}
          <div className="space-y-6">
            {/* AI Career & Study Advisor */}
            <div className="group bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden border border-white/5 hover:border-gold-primary/30 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] hover:-translate-y-2">
              <div className="flex items-center p-8 md:p-10">
                <div className="hidden md:block flex-shrink-0 w-56 h-56 rounded-2xl overflow-hidden shadow-2xl mr-10 border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop"
                    alt="AI Career Advisor"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" decoding="async"/>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white">
                      AI Career & Study Advisor
                    </h3>
                    <span className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold tracking-wider shadow-lg">
                      AI
                    </span>
                  </div>
                  <p className="font-outfit text-gray-400 text-lg leading-relaxed">
                    Get personalized guidance on scholarships, PR routes, job-seeker visas, and career planning from our intelligent AI mentor.
                  </p>
                </div>
              </div>
            </div>

            {/* Research Paper Analysis */}
            <div className="group bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden border border-white/5 hover:border-gold-primary/30 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] hover:-translate-y-2">
              <div className="flex items-center p-8 md:p-10">
                <div className="hidden md:block flex-shrink-0 w-56 h-56 rounded-2xl overflow-hidden shadow-2xl mr-10 border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=400&h=400&fit=crop"
                    alt="Research Papers"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" decoding="async"/>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white">
                      Research Paper Analysis
                    </h3>
                    <span className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold tracking-wider shadow-lg">
                      AI
                    </span>
                  </div>
                  <p className="font-outfit text-gray-400 text-lg leading-relaxed">
                    Upload research papers and get AI-powered summaries, explanations, and research proposal extraction assistance.
                  </p>
                </div>
              </div>
            </div>

            {/* GRE & GMAT Prep */}
            <div className="group bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden border border-white/5 hover:border-gold-primary/30 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] hover:-translate-y-2">
              <div className="flex items-center p-8 md:p-10">
                <div className="hidden md:block flex-shrink-0 w-56 h-56 rounded-2xl overflow-hidden shadow-2xl mr-10 border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&h=400&fit=crop"
                    alt="GRE GMAT Prep"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" decoding="async"/>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white">
                      GRE & GMAT Prep
                    </h3>
                    <span className="bg-gradient-to-r from-green-500 to-lime-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold tracking-wider shadow-lg">
                      GLOBAL
                    </span>
                  </div>
                  <p className="font-outfit text-gray-400 text-lg leading-relaxed">
                    Comprehensive question banks for GRE and GMAT with detailed explanations. Master quantitative, verbal, and analytical sections with AI-powered doubt solving.
                  </p>
                </div>
              </div>
            </div>

            {/* IIT-JEE & NEET-UG Success */}
            <div className="group bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden border border-white/5 hover:border-gold-primary/30 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] hover:-translate-y-2">
              <div className="flex items-center p-8 md:p-10">
                <div className="hidden md:block flex-shrink-0 w-56 h-56 rounded-2xl overflow-hidden shadow-2xl mr-10 border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400&h=400&fit=crop"
                    alt="Engineering Medical Prep"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" decoding="async"/>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white">
                      IIT-JEE & NEET-UG Success
                    </h3>
                    <span className="bg-gradient-to-r from-pink-500 to-rose-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold tracking-wider shadow-lg">
                      INDIA
                    </span>
                  </div>
                  <p className="font-outfit text-gray-400 text-lg leading-relaxed">
                    Complete preparation platform for IIT-JEE and NEET-UG aspirants with extensive MCQ banks and AI-powered doubt solving.
                  </p>
                </div>
              </div>
            </div>

            {/* Medical Exam Success Bank */}
            <div className="group bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden border border-white/5 hover:border-gold-primary/30 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] hover:-translate-y-2">
              <div className="flex items-center p-8 md:p-10">
                <div className="hidden md:block flex-shrink-0 w-56 h-56 rounded-2xl overflow-hidden shadow-2xl mr-10 border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=400&fit=crop"
                    alt="Medical Exams"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" decoding="async"/>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white">
                      Medical Exam Success Bank
                    </h3>
                    <span className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold tracking-wider shadow-lg">
                      GLOBAL
                    </span>
                  </div>
                  <p className="font-outfit text-gray-400 text-lg leading-relaxed">
                    Master USMLE, PLAB1, DHA, AMC, INBDE, ORE, and ADC exams with thousands of practice questions and detailed explanations.
                  </p>
                </div>
              </div>
            </div>

            {/* Dental Exam Preparation */}
            <div className="group bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden border border-white/5 hover:border-gold-primary/30 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] hover:-translate-y-2">
              <div className="flex items-center p-8 md:p-10">
                <div className="hidden md:block flex-shrink-0 w-56 h-56 rounded-2xl overflow-hidden shadow-2xl mr-10 border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=400&h=400&fit=crop"
                    alt="Dental Exams"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" decoding="async"/>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white">
                      Dental Exam Preparation
                    </h3>
                    <span className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold tracking-wider shadow-lg">
                      GLOBAL
                    </span>
                  </div>
                  <p className="font-outfit text-gray-400 text-lg leading-relaxed">
                    Comprehensive question banks for DHA Dental, INBDE, ORE Part 1, and ADC Part 1 exams with expert-curated content.
                  </p>
                </div>
              </div>
            </div>

            {/* Global Pharmacy Exams */}
            <div className="group bg-gradient-to-br from-[#111118] to-[#0a0a10] rounded-2xl overflow-hidden border border-white/5 hover:border-gold-primary/30 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(240,180,41,0.15)] hover:-translate-y-2">
              <div className="flex items-center p-8 md:p-10">
                <div className="hidden md:block flex-shrink-0 w-56 h-56 rounded-2xl overflow-hidden shadow-2xl mr-10 border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=400&h=400&fit=crop"
                    alt="Pharmacy Exams"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" decoding="async"/>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white">
                      Global Pharmacy Exams
                    </h3>
                    <span className="bg-gradient-to-r from-purple-500 to-violet-600 text-white px-4 py-1.5 rounded-lg text-xs font-bold tracking-wider shadow-lg">
                      GLOBAL
                    </span>
                  </div>
                  <p className="font-outfit text-gray-400 text-lg leading-relaxed">
                    Prepare for international pharmacy licensing exams including FPGEE, NAPLEX, PEBC, and other global pharmacy certification tests with comprehensive question banks.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ambition AI Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-primary/10 to-transparent"></div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-gold-primary/20 to-blue-primary/20 px-6 py-3 rounded-full mb-6 border border-white/10">
              <Sparkles className="w-6 h-6 text-gold-primary" />
              <span className="font-outfit text-lg font-bold text-white tracking-wide">Ambition AI</span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
              What is Your Ambition?
            </h2>
            <p className="font-outfit text-xl text-gray-400 font-medium">
              Let AI guide you to achieve your dreams with a personalized career roadmap
            </p>
          </div>

          <form onSubmit={handleAmbitionSubmit} className="space-y-6">
            <div className="relative">
              <textarea
                value={ambitionInput}
                onChange={(e) => setAmbitionInput(e.target.value)}
                placeholder="My ambition is to become a..."
                className="w-full h-48 p-6 bg-[#111118]/80 border-2 border-white/10 rounded-2xl text-white text-lg placeholder-gray-500 focus:border-gold-primary focus:outline-none focus:ring-4 focus:ring-gold-primary/20 transition-all duration-300 resize-none font-outfit backdrop-blur-xl"
                disabled={isLoadingAmbition}
              />
              <div className="absolute bottom-4 right-4 text-sm text-gray-500 font-outfit">
                {ambitionInput.length} / 1000
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={isLoadingAmbition || !ambitionInput.trim()}
              className="w-full bg-gradient-to-r from-gold-dark via-gold-primary to-gold-light hover:from-gold-light hover:via-gold-primary hover:to-gold-dark text-[#0a0a10] text-xl px-12 py-7 h-auto font-bold rounded-xl shadow-[0_8px_32px_rgba(240,180,41,0.4)] hover:shadow-[0_12px_48px_rgba(240,180,41,0.6)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingAmbition ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Analyzing Your Ambition...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-3">
                  <Brain className="w-6 h-6" />
                  Guide Me
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="font-outfit text-sm text-gray-500">
              🔒 Your ambition is private and secure • Powered by Advanced AI
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section (if stats exist) */}
      {stats && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0a0a10]/50 to-transparent">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold gradient-text-gold font-playfair mb-2">
                  {stats.totalUsers?.toLocaleString() || '10K+'}
                </div>
                <div className="text-gray-400 font-outfit font-medium">Active Learners</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold gradient-text-gold font-playfair mb-2">
                  {stats.totalCourses || '75+'}
                </div>
                <div className="text-gray-400 font-outfit font-medium">Courses</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold gradient-text-gold font-playfair mb-2">
                  {stats.totalExams || '12'}
                </div>
                <div className="text-gray-400 font-outfit font-medium">Exam Categories</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold gradient-text-gold font-playfair mb-2">
                  95%
                </div>
                <div className="text-gray-400 font-outfit font-medium">Success Rate</div>
              </div>
            </div>
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
};

export default LandingPage;
